import { FC, KeyboardEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import { TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { rtlDirectionForLocale } from 'src/utils/rtlLocales';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { getGrowthStageStatus, GROWTH_STAGE_COUNT } from '../getGrowthStageStatus';
import { PreviewLearnable } from '../useWordlistLocalsPreview';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'dashboard_next_lesson_words_title',
    defaultMessage: 'Lesson Preview',
  },
  nextLessonTitle: {
    id: 'dashboard_next_lesson_title',
    defaultMessage: 'In this lesson',
  },
  duration: {
    id: 'dashboard_next_lesson_duration',
    defaultMessage: '≈ {minutes} min',
  },
  hint: {
    id: 'dashboard_next_lesson_hint',
    defaultMessage: 'Pick any word to hear it, or watch a local say it',
  },
});

/** Rough lesson length: ~48s per preview word → rounds to the design's ≈4 min for 5 words. */
const estimateMinutes = (wordCount: number) => Math.max(1, Math.round(wordCount * 0.8));

export interface LocalsVideoPreviewProps {
  learnables: PreviewLearnable[];
  selectedId: number;
  onSelectLearnable?: (learnableId: number) => void;
  showNextLessonTitle?: boolean;
}

export const LocalsVideoPreview: FC<LocalsVideoPreviewProps> = ({
  learnables,
  selectedId,
  onSelectLearnable,
  showNextLessonTitle,
}) => {
  const intl = useIntl();
  const title = intl.formatMessage(showNextLessonTitle ? msgs.nextLessonTitle : msgs.title);

  const targetLanguage = getLocalStorageEntry(TARGET_LANGUAGE) ?? '';
  const targetLanguageDir = rtlDirectionForLocale(targetLanguage);

  const handleSelect = (learnableId: number) => {
    onSelectLearnable?.(learnableId);
  };

  const handleRowKeyDown = (event: KeyboardEvent, learnableId: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(learnableId);
    }
  };

  return (
    <S.Root>
      <S.TitleRow>
        <S.Title>{title}</S.Title>
        <S.Duration>
          <Icon name="timer" />
          {intl.formatMessage(msgs.duration, { minutes: estimateMinutes(learnables.length) })}
        </S.Duration>
      </S.TitleRow>
      <S.Hint>
        <Icon name="soundFull" />
        <Icon name="videoLibraryFilled" />
        <span>{intl.formatMessage(msgs.hint)}</span>
      </S.Hint>
      <S.WordList role="listbox" aria-label={title} data-testid="wordlist-locals-preview">
        {learnables.map(learnable => {
          const status = getGrowthStageStatus(learnable.growthLevel);
          const isSelected = learnable.id === selectedId;

          return (
            <S.WordRow
              key={learnable.id}
              role="option"
              tabIndex={0}
              aria-selected={isSelected}
              $isSelected={isSelected}
              onClick={() => handleSelect(learnable.id)}
              onKeyDown={event => handleRowKeyDown(event, learnable.id)}
              data-testid="wordlist-preview-word"
            >
              <S.WordText dir={targetLanguageDir}>
                <S.WordTerm>{learnable.target}</S.WordTerm>
                {learnable.gloss ? <S.WordGloss>{learnable.gloss}</S.WordGloss> : null}
              </S.WordText>
              <S.Stages>
                <S.StagesBar
                  role="progressbar"
                  aria-valuenow={status.filledStages}
                  aria-valuemin={0}
                  aria-valuemax={GROWTH_STAGE_COUNT}
                  aria-label={`${learnable.target} progress`}
                  data-testid="wordlist-preview-stages"
                >
                  {Array.from({ length: GROWTH_STAGE_COUNT }, (_, index) => (
                    <S.StageSeg key={index} $isOn={index < status.filledStages} aria-hidden />
                  ))}
                </S.StagesBar>
              </S.Stages>
            </S.WordRow>
          );
        })}
      </S.WordList>
    </S.Root>
  );
};
